import { ExpensePhase } from '@peles/shared';
import { useState, useCallback, useEffect } from 'react';
import { useCreateExpenseItem } from '../../hooks/useExpenses';
import { extractApiErrorMessage } from '../../utils/apiError';
import {
  NeedForm,
  EMPTY_EXPENSE_FORM,
  changedExpenseFields,
  readPrice,
  type ExpenseFormState,
} from './NeedForm';

interface CreateExpensePanelProps {
  eventId: string;
  onClose: () => void;
  onCreated: () => void;
  /**
   * `SOURCING` adds a need — a name, a category, a budget; vendors are asked
   * for quotes later. `BOOKED` (the default) is booking directly: the vendor
   * and price are asked for now.
   */
  phase?: 'SOURCING' | 'BOOKED';
  /** Told whenever the form goes from untouched to edited and back, so closing can ask first. */
  onUnsavedChange?: (dirty: boolean) => void;
}

/** Add an expense line to an event. The form is `NeedForm`; this owns creating it. */
export function CreateExpensePanel({
  eventId,
  onClose,
  onCreated,
  onUnsavedChange,
  phase = 'BOOKED',
}: CreateExpensePanelProps) {
  const sourcing = phase === 'SOURCING';
  const createExpenseItem = useCreateExpenseItem();
  const [form, setForm] = useState<ExpenseFormState>(EMPTY_EXPENSE_FORM);
  const [error, setError] = useState('');

  const dirty = changedExpenseFields(form, EMPTY_EXPENSE_FORM).length > 0;
  useEffect(() => {
    onUnsavedChange?.(dirty);
  }, [dirty, onUnsavedChange]);

  // Price is optional — a line can be added first and priced later — but a
  // typed one must be a number of at least 0.
  const price = readPrice(form.price);
  const budget = readPrice(form.budgetAmount);
  const isValid = form.vendorCategoryId !== '' && price !== 'invalid' && budget !== 'invalid';

  const handleCreate = useCallback(() => {
    if (price === 'invalid' || budget === 'invalid') return;
    setError('');
    createExpenseItem.mutate(
      {
        eventId,
        vendorCategoryId: form.vendorCategoryId,
        catalogItemId: form.catalogItemId || undefined,
        itemName: form.itemName.trim() || undefined,
        budgetAmount: budget ?? undefined,
        comments: form.comments.trim() || undefined,
        statusId: form.statusId || undefined,
        ...(sourcing
          ? { phase: ExpensePhase.SOURCING }
          : {
              vendorId: form.vendorId || undefined,
              vendorName: form.vendorName.trim() || undefined,
              price: price ?? undefined,
              dueDate: form.dueDate || undefined,
            }),
      },
      {
        onSuccess: () => onCreated(),
        onError: (err: unknown) =>
          setError(extractApiErrorMessage(err, 'Failed to create expense item')),
      },
    );
  }, [form, price, budget, sourcing, eventId, createExpenseItem, onCreated]);

  return (
    <div>
      {error && (
        <div role="alert" className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      <NeedForm
        value={form}
        onChange={setForm}
        idPrefix="create-expense"
        allowDefaultStatus
        onError={setError}
        sourcing={sourcing}
      />

      <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={!isValid || createExpenseItem.isPending}
          className="min-h-11 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {createExpenseItem.isPending ? 'Creating...' : sourcing ? 'Add need' : 'Add Expense'}
        </button>
      </div>
    </div>
  );
}
